import { createBrowserHistory, createHashHistory } from 'history';
import type { History } from 'history';
import type { Nullable } from '../types';

export enum HASH_TYPE {
  HASH_BANG_TYPE = 'hashbang',
  SLASH_TYPE = 'slash',
  NOSLASH_TYPE = 'noslash',
}

const initialValue = null;
let history: Nullable<History> = initialValue;

/**
 * Determine type of hast-routing
 * @param {string} basename - the base URL of the app
 * @returns {'noslash'|'slash'|'hashbang'} - type of hash-routing
 */
export const getHashType = (basename: string): HASH_TYPE => {
  if (basename.startsWith('#!/')) {
    return HASH_TYPE.HASH_BANG_TYPE;
  } else if (basename.startsWith('#/')) {
    return HASH_TYPE.SLASH_TYPE;
  } else {
    return HASH_TYPE.NOSLASH_TYPE;
  }
};

/**
 * Removes hash-prefix from string (supports: "hashbang", "slash" and "noslash" prefixes)
 * @param {string} basename - the base URL of the app
 * @returns {string} - trimmed string (without hash prefix)
 */
export const stripHashType = (basename: string): string =>
  basename.replace(/^(#!\/)|(#\/)|(#)/, '');

export const getHistory = () => history;

/**
 * Returns instance of browser or hash history (dynamically checks type of hash-based routing)
 * @param {string} basename - the base URL of the app
 * @param rest
 * @returns {{createHref: (function(*=): string), goBack: (function(): void), length: number, replace: replace, go: go, action: string, location, goForward: (function(): void), block: (function(): function(): void), push: push, listen}, null}
 */

interface Options {
  basename: string;
  [x: string]: any;
}

export const createHistory = ({ basename = '', ...rest }: Options) => {
  if (basename.startsWith('#')) {
    const hashType = getHashType(basename);
    const hashTypeBasename = stripHashType(basename);

    history = createHashHistory({
      hashType,
      basename: hashTypeBasename,
      ...rest,
    });
  } else {
    history = createBrowserHistory({ basename, ...rest });
  }

  return getHistory();
};

export const resetHistory = () => {
  history = initialValue;
};
